import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Search, Filter, X, Loader2, DollarSign, CalendarDays, Briefcase, Archive as ArchiveIcon, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { suiteLabel } from '@/lib/crmSuites';
import LocalizedDateInput from '@/components/shared/LocalizedDateInput';
import CreateCommercialLeadDialog from '@/components/admin/CreateCommercialLeadDialog';

const PIPELINE_STATUSES = [
  'lead', 'contacted', 'meeting_scheduled', 'proposal_sent',
  'negotiation', 'won', 'lost',
];

const STATUS_COLORS = {
  lead:              'bg-slate-100  text-slate-700  border-slate-200',
  contacted:         'bg-blue-50    text-blue-700   border-blue-200',
  meeting_scheduled: 'bg-indigo-50  text-indigo-700 border-indigo-200',
  proposal_sent:     'bg-purple-50  text-purple-700 border-purple-200',
  negotiation:       'bg-amber-50   text-amber-700  border-amber-200',
  won:               'bg-emerald-50 text-emerald-700 border-emerald-200',
  lost:              'bg-rose-50    text-rose-600   border-rose-200',
};

const fmtMoney = (n) => {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
};

/**
 * Iter 41.36 — Commercial CRM (Kanban board).
 *
 * - hm_admin sees every lead + Assigned Advisor on cards.
 * - hm_advisor sees only their own leads; advisor column hidden.
 * - Lead movement uses a per-card status dropdown (drag-and-drop deferred
 *   to avoid risky DnD lib in this iteration).
 */
export default function AdminCommercialCRM() {
  const { t } = useTranslation();
  const { user } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.role === 'hm_admin';

  const [leads, setLeads] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [filters, setFilters] = useState({
    assigned_advisor_id: searchParams.get('advisor') || '', country: '', industry: '',
    interested_suite: '', lead_source: '',
    follow_up_date_from: '', follow_up_date_to: '',
  });

  const reload = async () => {
    setLoading(true);
    try {
      const params = { ...(q ? { q } : {}), ...(showArchived ? { archived: true } : {}) };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [list, mx] = await Promise.all([
        adminApi.listCommercialLeads(params),
        adminApi.commercialMetrics().catch(() => null),
      ]);
      setLeads(Array.isArray(list) ? list : []);
      setMetrics(mx);
      if (isAdmin && advisors.length === 0) {
        try { setAdvisors(await adminApi.listAdvisors()); } catch { /* non-fatal */ }
      }
    } catch (e) {
      toast.error(t('pages.crm.loadFailed', { defaultValue: 'Failed to load leads' }), {
        description: formatApiError(e),
      });
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filters, q, showArchived]);

  const restoreLead = async (lead) => {
    setRestoringId(lead.id);
    try {
      await adminApi.restoreEntity('commercial_lead', lead.id);
      toast.success(t('pages.crm.archive.restored', { defaultValue: 'Opportunity restored' }));
      reload();
    } catch (e) {
      toast.error(t('pages.crm.archive.restoreFailed', { defaultValue: 'Could not restore' }), {
        description: formatApiError(e),
      });
    } finally { setRestoringId(null); }
  };

  const advisorById = useMemo(() => Object.fromEntries(advisors.map((a) => [a.id, a])), [advisors]);

  const grouped = useMemo(() => {
    const out = Object.fromEntries(PIPELINE_STATUSES.map((s) => [s, []]));
    leads.forEach((l) => {
      const k = PIPELINE_STATUSES.includes(l.pipeline_status) ? l.pipeline_status : 'lead';
      out[k].push(l);
    });
    return out;
  }, [leads]);

  const changeStatus = async (lead, newStatus) => {
    if (newStatus === lead.pipeline_status) return;
    try {
      await adminApi.patchCommercialLead(lead.id, { pipeline_status: newStatus });
      toast.success(t('pages.crm.statusUpdated', { defaultValue: 'Stage updated' }));
      reload();
    } catch (e) {
      toast.error(t('pages.crm.statusUpdateFailed', { defaultValue: 'Could not update stage' }), {
        description: formatApiError(e),
      });
    }
  };

  const clearFilters = () => setFilters({
    assigned_advisor_id: '', country: '', industry: '',
    interested_suite: '', lead_source: '',
    follow_up_date_from: '', follow_up_date_to: '',
  });

  const hasActiveFilters = Object.values(filters).some(Boolean) || !!q;

  return (
    <>
      <Topbar
        title={t('pages.crm.title', { defaultValue: 'Commercial CRM' })}
        subtitle={t('pages.crm.subtitle', { defaultValue: 'Sales pipeline and commercial follow-up.' })}
      />
      <div className="p-6 lg:p-8 space-y-6" data-testid="crm-page">
        {/* Toolbar */}
        <Card className="p-4 shadow-card flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('pages.crm.searchPh', { defaultValue: 'Search by company or contact…' })}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-9"
              data-testid="crm-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button variant={showArchived ? 'default' : 'outline'} size="sm"
                    onClick={() => setShowArchived((v) => !v)}
                    data-testid="crm-archived-toggle">
              <ArchiveIcon className="h-4 w-4 mr-1.5" />
              {showArchived
                ? t('pages.crm.archive.showActive', { defaultValue: 'View active' })
                : t('pages.crm.archive.showArchived', { defaultValue: 'View archived' })}
            </Button>
            <Button onClick={() => setCreateOpen(true)}
                    className="bg-gradient-primary hover:opacity-90"
                    data-testid="crm-create-btn">
              <Plus className="h-4 w-4 mr-1.5" />
              {t('pages.crm.newLead', { defaultValue: 'New lead' })}
            </Button>
          </div>
        </Card>

        {/* KPIs */}
        {metrics && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="crm-kpis">
            <Card className="p-4 shadow-card">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                {t('pages.crm.kpi.total', { defaultValue: 'Total leads' })}
              </p>
              <p className="text-2xl font-bold tabular-nums mt-0.5" data-testid="crm-kpi-total">{metrics.totals.count}</p>
            </Card>
            <Card className="p-4 shadow-card">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                {t('pages.crm.kpi.pipelineValue', { defaultValue: 'Pipeline value' })}
              </p>
              <p className="text-2xl font-bold tabular-nums mt-0.5" data-testid="crm-kpi-value">{fmtMoney(metrics.totals.open_value_total ?? metrics.totals.value_total)}</p>
            </Card>
            <Card className="p-4 shadow-card">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                {t('pages.crm.kpi.won', { defaultValue: 'Won' })}
              </p>
              <p className="text-2xl font-bold tabular-nums mt-0.5" data-testid="crm-kpi-won">{metrics.by_status?.won?.count ?? 0}</p>
            </Card>
            <Card className="p-4 shadow-card">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                {t('pages.crm.kpi.negotiation', { defaultValue: 'In negotiation' })}
              </p>
              <p className="text-2xl font-bold tabular-nums mt-0.5" data-testid="crm-kpi-negotiation">{metrics.by_status?.negotiation?.count ?? 0}</p>
            </Card>
          </div>
        )}

        {/* Filters — Founder UAT (Finding G): explicit labels, truthful date
            semantics (the range filters the lead's follow-up date). */}
        <Card className="p-4 shadow-card" data-testid="crm-filters">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {isAdmin && (
              <div>
                <Label className="text-xs text-muted-foreground">{t('pages.crm.filter.advisor', { defaultValue: 'Advisor' })}</Label>
                <Select value={filters.assigned_advisor_id || 'all'}
                        onValueChange={(v) => setFilters((f) => ({ ...f, assigned_advisor_id: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="h-9 mt-1" data-testid="crm-filter-advisor"><SelectValue placeholder={t('pages.crm.filter.advisor', { defaultValue: 'Advisor' })} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('pages.crm.filter.allAdvisors', { defaultValue: 'All advisors' })}</SelectItem>
                    {advisors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs text-muted-foreground">{t('pages.crm.filter.source', { defaultValue: 'Lead source' })}</Label>
              <Select value={filters.lead_source || 'all'}
                      onValueChange={(v) => setFilters((f) => ({ ...f, lead_source: v === 'all' ? '' : v }))}>
                <SelectTrigger className="h-9 mt-1" data-testid="crm-filter-source"><SelectValue placeholder={t('pages.crm.filter.source', { defaultValue: 'Lead source' })} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('pages.crm.filter.allSources', { defaultValue: 'All sources' })}</SelectItem>
                  {['website_request', 'referral', 'cold_outreach', 'linkedin', 'existing_client', 'event', 'conference'].map((s) => (
                    <SelectItem key={s} value={s}>{t(`pages.crm.source.${s}`, { defaultValue: s })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('pages.crm.filter.country', { defaultValue: 'Country' })}</Label>
              <Input value={filters.country}
                     onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
                     placeholder={t('pages.crm.filter.country', { defaultValue: 'Country' })}
                     className="h-9 mt-1"
                     data-testid="crm-filter-country" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('pages.crm.filter.industry', { defaultValue: 'Industry' })}</Label>
              <Input value={filters.industry}
                     onChange={(e) => setFilters((f) => ({ ...f, industry: e.target.value }))}
                     placeholder={t('pages.crm.filter.industry', { defaultValue: 'Industry' })}
                     className="h-9 mt-1"
                     data-testid="crm-filter-industry" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('pages.crm.filter.followUpFrom', { defaultValue: 'Follow-up from' })}</Label>
              <LocalizedDateInput
                value={filters.follow_up_date_from}
                onChange={(v) => setFilters((f) => ({ ...f, follow_up_date_from: v }))}
                className="mt-1"
                data-testid="crm-filter-followup-from" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('pages.crm.filter.followUpTo', { defaultValue: 'Follow-up to' })}</Label>
              <LocalizedDateInput
                value={filters.follow_up_date_to}
                onChange={(v) => setFilters((f) => ({ ...f, follow_up_date_to: v }))}
                className="mt-1"
                data-testid="crm-filter-followup-to" />
            </div>
          </div>
          <div className="flex justify-end mt-2">
            <Button size="sm" variant="ghost" disabled={!hasActiveFilters}
                    onClick={() => { clearFilters(); setQ(''); }}
                    data-testid="crm-filters-clear">
              <X className="h-4 w-4 mr-1" />
              {t('pages.crm.filter.clear', { defaultValue: 'Clear filters' })}
            </Button>
          </div>
        </Card>

        {/* Kanban / Archived list */}
        {loading ? (
          <div className="py-16 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
          </div>
        ) : showArchived ? (
          <Card className="p-4 shadow-card" data-testid="crm-archived-list">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <ArchiveIcon className="h-4 w-4" />
              {t('pages.crm.archive.listTitle', { defaultValue: 'Archived opportunities' })}
            </h3>
            {leads.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-8 text-center" data-testid="crm-archived-empty">
                {t('pages.crm.archive.archivedEmpty', { defaultValue: 'No archived opportunities.' })}
              </p>
            ) : (
              <div className="space-y-2">
                {leads.map((lead) => (
                  <div key={lead.id}
                       className="flex items-center gap-3 flex-wrap border rounded-lg p-3 hover:bg-muted/30 transition"
                       data-testid={`crm-archived-row-${lead.id}`}>
                    <button type="button" className="flex-1 min-w-[200px] text-left"
                            onClick={() => navigate(`/admin/crm/${lead.id}`)}>
                      <p className="font-semibold text-sm">{lead.company_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {lead.primary_contact_name}
                        {lead.estimated_value != null ? ` · ${fmtMoney(lead.estimated_value)}` : ''}
                      </p>
                    </button>
                    <Badge variant="outline" className={STATUS_COLORS[lead.pipeline_status] || ''}>
                      {t(`pages.crm.status.${lead.pipeline_status}`)}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {lead.archive_state?.archived_at
                        ? new Date(lead.archive_state.archived_at).toLocaleDateString()
                        : ''}
                    </span>
                    {isAdmin && (
                      <Button size="sm" variant="outline" disabled={restoringId === lead.id}
                              onClick={() => restoreLead(lead)}
                              data-testid={`crm-restore-${lead.id}`}>
                        {restoringId === lead.id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <><RotateCcw className="h-3.5 w-3.5 mr-1" />{t('pages.crm.archive.restore', { defaultValue: 'Restore' })}</>}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        ) : (
          <div className="overflow-x-auto -mx-2 px-2" data-testid="crm-kanban">
            <div className="grid grid-flow-col auto-cols-[280px] gap-3 min-w-min">
              {PIPELINE_STATUSES.map((status) => (
                <div key={status} className="flex flex-col" data-testid={`crm-col-${status}`}>
                  <div className={`px-3 py-2 rounded-t-lg border ${STATUS_COLORS[status]} flex items-center justify-between`}>
                    <h3 className="font-semibold text-sm">
                      {t(`pages.crm.status.${status}`)}
                    </h3>
                    <Badge variant="outline" className="bg-white/60 text-xs tabular-nums" data-testid={`crm-col-count-${status}`}>
                      {grouped[status].length}
                    </Badge>
                  </div>
                  <div className="flex-1 bg-muted/30 rounded-b-lg border-x border-b p-2 space-y-2 min-h-[120px]">
                    {grouped[status].length === 0 ? (
                      <p className="text-xs text-muted-foreground italic text-center py-6">
                        {t('pages.crm.emptyColumn', { defaultValue: 'No leads' })}
                      </p>
                    ) : grouped[status].map((lead) => (
                      <Card key={lead.id}
                            className="p-3 shadow-sm hover:shadow-md transition-smooth cursor-pointer space-y-2"
                            onClick={() => navigate(`/admin/crm/${lead.id}`)}
                            data-testid={`crm-card-${lead.id}`}>
                        <div>
                          <p className="font-semibold text-sm truncate">{lead.company_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{lead.primary_contact_name}</p>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {lead.estimated_value != null && (
                            <span className="inline-flex items-center gap-1 tabular-nums">
                              <DollarSign className="h-3 w-3" /> {fmtMoney(lead.estimated_value)}
                            </span>
                          )}
                          {lead.follow_up_date && (
                            <span className="inline-flex items-center gap-1">
                              <CalendarDays className="h-3 w-3" /> {lead.follow_up_date}
                            </span>
                          )}
                        </div>
                        {(lead.interested_suites || []).length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {lead.interested_suites.slice(0, 3).map((s) => (
                              <Badge key={s} variant="outline" className="text-xs px-1.5 py-0">{suiteLabel(s)}</Badge>
                            ))}
                          </div>
                        )}
                        {isAdmin && lead.assigned_advisor_id && (
                          <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
                            <Briefcase className="h-3 w-3" />
                            {advisorById[lead.assigned_advisor_id]?.name ||
                             t('pages.crm.unassigned', { defaultValue: 'Unassigned' })}
                          </p>
                        )}
                        <Select value={lead.pipeline_status}
                                onValueChange={(v) => changeStatus(lead, v)}>
                          <SelectTrigger className="h-7 text-xs"
                                         onClick={(e) => e.stopPropagation()}
                                         data-testid={`crm-card-status-${lead.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PIPELINE_STATUSES.map((s) => (
                              <SelectItem key={s} value={s}>{t(`pages.crm.status.${s}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <CreateCommercialLeadDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={reload}
        advisors={advisors}
        isAdmin={isAdmin}
      />
    </>
  );
}
